/**
 * OSMDExerciseScene.grader - the real per-take grade rule (MUS-320).
 *
 * Pure, Phaser-free: it takes the take's per-note final band states and returns a
 * verdict + a gradient readout. The scene adapter (OSMDExerciseScene) supplies the
 * band states by reading the rendered notes' NoteContainer states at grade time; it
 * does not modify the shared OSMDEventStream / NoteContainer classes.
 *
 * The rule (band composition with a green-majority clearance floor; supersedes the
 * earlier failed/clean/flawless band-boundary framing):
 *   - Key off the band COMPOSITION, never a raw accuracy %.
 *       green  = right note, in time.
 *       yellow = right note, early/late (timing loose).
 *       red    = wrong / missed / extra / never-played note.
 *   - anyRed  (>= 1 red)                    -> 'failed'   (does NOT clear; stays Attempted).
 *   - zero red AND green > yellow           -> 'clean'    (Cleared-eligible: all correct notes,
 *                                                          a green majority; timing loose on a
 *                                                          minority of notes).
 *   - zero red AND green <= yellow          -> 'failed'   (yellow-majority OR a green == yellow
 *                                                          tie - the all-yellow reductio - is
 *                                                          too loose to clear; stays Attempted).
 *   - allGreen (zero yellow AND zero red)   -> 'flawless' (Mastered-eligible via the streak
 *                                                          policy the machine owns).
 *   The clearance floor is a bare green majority: no yellow
 *   budget and no tunable green-fraction bar - green > yellow, full stop; flawless = all green.
 *
 * `accuracy` is a DERIVED READOUT ONLY (the green fraction), carried for the gradient
 * signal. It is derived from the SAME per-note bands as the gate and is always shown
 * alongside the green/yellow/red breakdown and the cell status, never on its own. A high
 * green fraction CAN sit beside a 'failed' take (one red among many greens still fails);
 * the single guarantee is accuracy === 100 iff flawless, so the number never reads 100%
 * on a non-flawless take. The gate is the composition, not the number - do not re-key on
 * `accuracy`.
 */

import type { NoteState } from '../../vexflow-phaser-backend/NoteContainer';
import type { Verdict } from 'Types/ExerciseContentTypes';

export type Band = 'green' | 'yellow' | 'red';

/** The green/yellow/red note breakdown for one take (the gradient's core). */
export interface BandBreakdown {
  green: number;
  yellow: number;
  red: number;
  /** Total notes graded = green + yellow + red. */
  total: number;
}

/** The full computed outcome of one take. */
export interface GradeComputation {
  breakdown: BandBreakdown;
  anyRed: boolean;
  /** Zero yellow AND zero red over a non-empty take. */
  allGreen: boolean;
  /** The internal transport enum the machine consumes (not a user-facing pass/fail). */
  verdict: Verdict;
  /** Derived readout only: green fraction, 0..100, rounded. Never the gate. */
  accuracy: number;
  /**
   * True iff the take received no judged input at all (every rendered note is still
   * in an unresolved state - default/upcoming/focused). This does NOT change the
   * verdict (a no-input take is still 'failed' and still does not clear); it only
   * lets the visible signal label the take honestly as "no input" instead of a
   * misleading all-red gradient. Relevant pre-MUS-319 (no MIDI drives the stream).
   */
  noInput: boolean;
}

/**
 * A note whose judge never resolved it to a play result. At grade time (after a full
 * take) an unresolved note is a note the player never landed -> a miss -> red.
 */
const isUnplayed = (state: NoteState): boolean =>
  state === 'default' || state === 'upcoming' || state === 'focused';

/**
 * Map one rendered note's final NoteContainer state to its band.
 *   correct        -> green
 *   early | late   -> yellow (right note, timing off)
 *   missed         -> red (wrong / missed / extra: makeNoteError sets 'missed')
 *   default/...    -> red  (never-played at grade time is a miss)
 */
export const noteStateToBand = (state: NoteState): Band => {
  switch (state) {
    case 'correct':
      return 'green';
    case 'early':
    case 'late':
      return 'yellow';
    case 'missed':
      return 'red';
    case 'default':
    case 'upcoming':
    case 'focused':
      // Never resolved at grade time => never played => a miss => red.
      return 'red';
    default: {
      // Exhaustiveness guard: with all seven NoteState values handled above, `state`
      // narrows to `never` here and this compiles. If NoteState later gains a value,
      // this line stops compiling - vocabulary drift becomes a build error rather than a
      // silent mis-map. Runtime still degrades to red (an unknown state is a note that
      // never resolved); this grade path is not try/caught, so it must not throw.
      const _exhaustive: never = state;
      void _exhaustive;
      return 'red';
    }
  }
};

/** Tally an already-mapped band list. Exported for the composition unit tests. */
export const gradeBands = (bands: Band[]): GradeComputation => {
  let green = 0;
  let yellow = 0;
  let red = 0;
  for (const band of bands) {
    if (band === 'green') green += 1;
    else if (band === 'yellow') yellow += 1;
    else red += 1;
  }
  const total = green + yellow + red;

  const anyRed = red > 0;
  const allGreen = total > 0 && red === 0 && yellow === 0;

  // Band composition with the green-majority floor. Order matters:
  //   - any red (or an empty take) fails outright;
  //   - among zero-red takes, all-green is flawless;
  //   - a zero-red take with a green majority (green > yellow) is clean (Cleared-eligible);
  //   - a zero-red take with green <= yellow (yellow-majority OR a green == yellow tie,
  //     the all-yellow reductio included) does NOT clear -> failed.
  // The floor tightens ONLY the clean predicate: the earlier "zero red, any number of yellows
  // -> clean" becomes "zero red AND green > yellow -> clean". The floor is a bare 50%
  // majority - parameter-free, length-invariant, no yellow budget, no small-N collapse
  // (one yellow on eight still clears; all-yellow never clears). Ties do not clear.
  let verdict: Verdict;
  if (total === 0 || anyRed) {
    verdict = 'failed';
  } else if (allGreen) {
    verdict = 'flawless';
  } else if (green > yellow) {
    verdict = 'clean';
  } else {
    // zero red, but green <= yellow (yellow-majority or tie): timing too loose to clear.
    verdict = 'failed';
  }

  const accuracy = total > 0 ? Math.round((green / total) * 100) : 0;

  return {
    breakdown: { green, yellow, red, total },
    anyRed,
    allGreen,
    verdict,
    accuracy,
    noInput: false,
  };
};

/**
 * Grade a take from the rendered notes' final NoteContainer states.
 *
 * This is the scene-facing entry point: the scene reads
 * `getStaveNoteContainers().map((c) => c.getState())` and hands the states here.
 * Never-played notes fold into red for the verdict; if EVERY note is unresolved the
 * take is flagged `noInput` for the signal (verdict stays 'failed').
 */
export const gradeNoteStates = (states: NoteState[]): GradeComputation => {
  const computation = gradeBands(states.map(noteStateToBand));
  const noInput = states.length > 0 && states.every(isUnplayed);
  return { ...computation, noInput };
};

/** One rendered note's final state and the hand that plays it. */
export interface HandNoteState {
  hand: 'RH' | 'LH';
  state: NoteState;
}

/**
 * The grade a counted take is stored with: the green fraction (0..1) of the notes the take was
 * graded on, overall and for each hand (null for a hand with no notes). It is read from the
 * same note states as the verdict, so a stored grade always matches the verdict counted.
 */
export interface CountedGrade {
  combined: number | null;
  left: number | null;
  right: number | null;
}

const greenFraction = (states: readonly NoteState[]): number | null =>
  states.length === 0
    ? null
    : states.filter((state) => noteStateToBand(state) === 'green').length / states.length;

/** The counted grade from all the take's note states, and the same notes by hand. */
export const countedGrade = (
  states: readonly NoteState[],
  byHand: readonly HandNoteState[],
): CountedGrade => ({
  combined: greenFraction(states),
  left: greenFraction(byHand.filter((note) => note.hand === 'LH').map((note) => note.state)),
  right: greenFraction(byHand.filter((note) => note.hand === 'RH').map((note) => note.state)),
});
